import { zodResolver } from '@hookform/resolvers/zod'
import { Shield } from 'lucide-react'
import { useCompanyPositionTemplates } from 'queries/company/useCompanyPositionTemplates'
import { useUpdateWorkerStatusForCompanyMutation } from 'queries/worker'
import { usePositionTemplateBlockMutation } from 'queries/worker/usePositionTemplateBlockMutation'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import {
  Box,
  Checkbox,
  Field,
  Flex,
  Heading,
  Input,
  Modal,
  Text,
  Textarea,
  toast,
} from 'ui'
import { z } from 'zod'
import { BlockModalProps } from './types'
import { useAuthContext } from 'components/auth'

const MIN_REASON_CHARACTERS = 25

const blockSchema = z.object({
  reason: z.string().min(MIN_REASON_CHARACTERS, {
    message: `Reason must be at least ${MIN_REASON_CHARACTERS} characters`,
  }),
  positionTemplateIds: z.record(z.coerce.number(), z.coerce.boolean()),
})

type BlockFormValues = z.infer<typeof blockSchema>

type BlockValue = Record<number, boolean>

type Props = BlockModalProps & {
  worker: {
    positionTemplateBlocks: {
      positionTemplateId: number
      blocked: boolean
    }[]
    id: number
    name?: string
  }
}

export function CompanyPositionTemplateBlock(props: Props) {
  const { isOpen, worker, onClose } = props

  const [selectAll, setSelectAll] = useState<boolean>(false)
  const [search, setSearch] = useState<string>('')

  const { company } = useAuthContext()

  const {
    data: positionTemplates = [],
    isLoading: isPositionTemplatesLoading,
  } = useCompanyPositionTemplates()
  const {
    mutate: blockPositionTemplates,
    isPending: isBlockPositionTemplatesPending,
  } = usePositionTemplateBlockMutation(worker.id)

  const initialValues = useMemo(() => {
    return (
      worker.positionTemplateBlocks?.reduce(
        (acc, block) => ({
          ...acc,
          [block.positionTemplateId]: block.blocked,
        }),
        {} satisfies BlockValue
      ) ?? {}
    )
  }, [worker.positionTemplateBlocks])

  const { setValue, watch, control, handleSubmit, reset, getValues } =
    useForm<BlockFormValues>({
      resolver: zodResolver(blockSchema),
      defaultValues: {
        reason: '',
        positionTemplateIds: initialValues,
      },
    })

  // Reset form when initialValues load (e.g. worker profile fetched after mount)
  useEffect(() => {
    if (Object.keys(initialValues).length > 0) {
      reset({
        reason: getValues('reason'),
        positionTemplateIds: initialValues,
      })
    }
  }, [initialValues, reset, getValues])

  const searchResults = positionTemplates
    .filter((positionTemplate) =>
      positionTemplate.name.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => a.name.localeCompare(b.name))

  // Manage Select All
  const positionTemplateIds = watch('positionTemplateIds', {})
  const selectedPositionCount =
    Object.values(positionTemplateIds).filter(Boolean).length

  const handleSelectAll = () => {
    const isSelectAll = !selectAll
    if (isSelectAll) {
      setValue('positionTemplateIds', {})
      setSearch('')
    }
    setSelectAll(isSelectAll)
  }

  const { mutate: updateStatus, isPending: isCompanyBlockPending } =
    useUpdateWorkerStatusForCompanyMutation(worker.id, {
      onSuccess: () => {
        onClose?.()
      },
      onError: (err) => {
        toast.error(err?.message ?? 'Failed to update worker')
      },
    })

  const onSubmit = () => {
    return handleSubmit((data: BlockFormValues) => {
      if (!company?.id) {
        toast.error('Unauthenticated')
        return
      }

      if (selectAll) {
        updateStatus({
          action: 'block',
          reason: data.reason,
        })
        return
      }

      blockPositionTemplates(
        {
          companyId: company.id,
          workerId: worker.id,
          reason: data.reason,
          positionTemplateBlocks: Object.entries(data.positionTemplateIds)
            .filter(([, isSelected]) => isSelected)
            .map(([id]) => ({
              positionTemplateId: Number(id),
              blocked: true,
            })),
        },
        {
          onSuccess: () => {
            onClose?.()
          },
          onError: (err) => {
            toast.error(err?.message ?? 'Failed to update worker')
          },
        }
      )
    })()
  }

  const isLoading =
    isCompanyBlockPending ||
    isBlockPositionTemplatesPending ||
    isPositionTemplatesLoading

  return (
    <Modal
      title={
        <Flex gap={2} alignItems="center">
          <Flex
            alignItems="center"
            justifyContent="center"
            p={2}
            borderRadius="standard"
            backgroundColor="error50"
            mr={1}
          >
            <Shield size={24} color="red" />
          </Flex>
          <Box>
            <Heading level={2} mb={0}>
              Block / Unblock Worker
            </Heading>
          </Box>
        </Flex>
      }
      loading={isLoading}
      open={isOpen}
      onClose={() => onClose()}
      okText={
        selectedPositionCount || selectAll ? 'Block Worker' : 'Unblock Worker'
      }
      onOk={() => {
        void onSubmit()
      }}
    >
      <form onSubmit={onSubmit} style={{ width: '100%' }} autoComplete="off">
        <Flex flexDirection="column" gap={4}>
          <Flex flexDirection="column" gap={3}>
            <Box>
              <Heading level={4}>Select Job Types to Block</Heading>
              <Text fontSize={1} color="lightText" mt={-2}>
                Choose which job types this worker should be blocked from.
              </Text>
            </Box>
            {/** Search box */}
            <Box width="100%">
              <Input
                disabled={selectAll}
                block
                variant="small"
                type="text"
                placeholder="Search job types..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Box>
            {/** Select Position Templates */}
            <Flex flexDirection="column" gap={2}>
              <Box
                backgroundColor="offWhite"
                p={3}
                borderRadius="standard"
                borderLeft="4px solid"
                borderColor="error"
              >
                <Checkbox
                  key="all"
                  value={selectAll}
                  /** @ts-ignore-error Checkbox type definition does not infer from value */
                  onChange={handleSelectAll}
                >
                  Select All — block from all shifts at my company
                </Checkbox>
              </Box>

              {!selectAll && (
                <Box>
                  {searchResults.map((positionTemplate) => (
                    <Box
                      width="50%"
                      display="inline-block"
                      my={2}
                      paddingRight={2}
                      key={positionTemplate.id}
                    >
                      <Controller<BlockFormValues, 'positionTemplateIds'>
                        control={control}
                        name={`positionTemplateIds.${positionTemplate.id}`}
                        render={({ field, fieldState: { error } }) => (
                          <Field error={error?.message}>
                            <Checkbox
                              value={field.value ?? false}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                            >
                              {positionTemplate.name}
                            </Checkbox>
                          </Field>
                        )}
                      />
                    </Box>
                  ))}

                  <Flex
                    gap={2}
                    alignItems="center"
                    backgroundColor={'offWhite'}
                    p={2}
                    borderRadius="standard"
                    justifyContent="flex-end"
                    mt={2}
                  >
                    <Text fontWeight="600">{selectedPositionCount}</Text>
                    <Text fontSize={1} color="lightText">
                      {' '}
                      selected
                    </Text>
                  </Flex>
                </Box>
              )}
            </Flex>
          </Flex>

          <Box>
            <Heading level={4} mb={2}>
              What will happen when you block this worker?
            </Heading>
            {selectAll ? (
              <>
                <Text fontWeight="500" fontSize={1} color="text" mb={1}>
                  Blocking from entire company:
                </Text>
                <ul style={{ listStyle: 'disc', margin: '0.75em 0 0 1.5em' }}>
                  <li>
                    <Text fontSize={1} color="lightText" mt={1}>
                      This worker will be removed from all scheduled shifts at
                      your company.
                    </Text>
                  </li>
                  <li>
                    <Text fontSize={1} color="lightText" mt={1}>
                      They will not be able to accept any shifts at your company
                      going forward.
                    </Text>
                  </li>
                  <li>
                    <Text fontSize={1} color="lightText" mt={1}>
                      The worker will not be notified about this block.
                    </Text>
                  </li>
                </ul>
              </>
            ) : (
              <>
                <Text fontWeight="500" fontSize={1} color="text" mb={1}>
                  Blocking from selected job types:
                </Text>
                <ul style={{ listStyle: 'disc', margin: '0.75em 0 0 1.5em' }}>
                  <li>
                    <Text fontSize={1} color="lightText" mt={1}>
                      Will be removed from all scheduled shifts for the selected
                      job types at your company.
                    </Text>
                  </li>
                  <li>
                    <Text fontSize={1} color="lightText" mt={1}>
                      Will no longer be able to accept new shifts for those job
                      types when posted by your company.
                    </Text>
                  </li>
                  <li>
                    <Text fontSize={1} color="lightText" mt={1}>
                      Will not notify the worker about this block.
                    </Text>
                  </li>
                </ul>
              </>
            )}
          </Box>

          <Box>
            <Heading level={4} mb={1}>
              Reason for{' '}
              {selectedPositionCount || selectAll ? 'Blocking' : 'Unblocking'}
            </Heading>
            <Text fontSize={1} color="lightText">
              Provide a detailed explanation for why you're{' '}
              {selectedPositionCount || selectAll ? 'un' : ''}blocking this
              worker.
            </Text>
            <Box mt={2}>
              <Controller<BlockFormValues, 'reason'>
                name="reason"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Field error={error?.message}>
                    <Textarea
                      minRows={2}
                      {...field}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={`Please provide a detailed reason for ${
                        selectedPositionCount || selectAll ? 'un' : ''
                      }blocking (e.g., missed shift, misconduct)`}
                    />
                  </Field>
                )}
              />
              <Text color={'lightText'} fontSize={1} mt={1} textAlign={'right'}>
                {watch('reason').trim().length} characters (minimum{' '}
                {MIN_REASON_CHARACTERS} characters)
              </Text>
            </Box>
          </Box>
        </Flex>
      </form>
    </Modal>
  )
}
